import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats]         = useState({ carpenters: 0, labours: 0, total: 0 });
  const [adminName, setAdminName] = useState("Admin");
  const [loading, setLoading]     = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/admin/stats");
        setStats({ carpenters: res.data.carpenters, labours: res.data.labours, total: res.data.totalUsers });
        if (res.data.adminName) setAdminName(res.data.adminName.split(" ")[0]);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  const cards = [
    { label:"Total Carpenters",    value:stats.carpenters, emoji:"🪚", color:"#D97706", bg:"#FEF3C7", border:"#FDE68A", desc:"Registered carpenters",    path:"/admin/users?role=carpenter" },
    { label:"Total Labours",       value:stats.labours,    emoji:"🔨", color:"#0369A1", bg:"#E0F2FE", border:"#BAE6FD", desc:"Labour accounts",          path:"/admin/users?role=labour" },
    { label:"Total Users",         value:stats.total,      emoji:"👥", color:"#059669", bg:"#D1FAE5", border:"#A7F3D0", desc:"All platform members",      path:"/admin/users" },
    { label:"Carpenter & Labour",  value:null,             emoji:"🔗", color:"#7C3AED", bg:"#EDE9FE", border:"#DDD6FE", desc:"View who manages whom",     path:"/admin/carpenter-labours", cta:true },
  ];

  const quickActions = [
    { label:"All Users",           emoji:"👥", action: () => navigate("/admin/users") },
    { label:"Carpenters",          emoji:"🪚", action: () => navigate("/admin/users?role=carpenter") },
    { label:"Labours",             emoji:"🔨", action: () => navigate("/admin/users?role=labour") },
    { label:"Carpenter & Labour",  emoji:"🔗", action: () => navigate("/admin/carpenter-labours") },
    { label:"Trash",               emoji:"🗑️", action: () => navigate("/admin/trash") },
  ];

  const chartData = {
    labels: ["Carpenters", "Labours"],
    datasets: [{ data: [stats.carpenters, stats.labours], backgroundColor: ["#D97706", "#0369A1"], borderWidth: 0 }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { family: "'Plus Jakarta Sans', sans-serif", size: 13, weight: "600" }, padding: 20, usePointStyle: true, pointStyleWidth: 10 },
      },
      tooltip: {
        callbacks: { label: (ctx) => { const t = ctx.dataset.data.reduce((a,b)=>a+b,0); const p = t ? Math.round((ctx.parsed/t)*100) : 0; return `  ${ctx.label}: ${ctx.parsed} (${p}%)`; } },
        bodyFont: { family: "'Plus Jakarta Sans', sans-serif", size: 13 },
        padding: 10, cornerRadius: 8,
      },
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        *,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Plus Jakarta Sans',sans-serif; background:#FAF7F4; }
        .stat-card { background:#fff; border:1.5px solid #E7E5E4; border-radius:16px; padding:20px; cursor:pointer; position:relative; overflow:hidden; animation:fadeUp .4s ease both; transition:transform .2s,box-shadow .2s,border-color .2s; }
        .stat-card:hover { transform:translateY(-3px); box-shadow:0 12px 32px rgba(0,0,0,.09); border-color:#D97706; }
        .nav-btn { display:flex; align-items:center; gap:9px; width:100%; padding:10px 12px; border-radius:10px; border:none; background:none; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; font-weight:600; color:#78716C; cursor:pointer; transition:background .15s,color .15s; text-align:left; }
        .nav-btn:hover,.nav-btn.active { background:#FEF3C7; color:#D97706; }
        .nav-btn.danger:hover { background:#FEF2F2; color:#DC2626; }
        .qa-btn { display:flex; align-items:center; gap:8px; padding:10px 16px; border-radius:10px; border:1.5px solid #E7E5E4; background:#fff; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#44403C; cursor:pointer; transition:all .15s; white-space:nowrap; }
        .qa-btn:hover { background:#FEF3C7; border-color:#D97706; color:#92400E; }
        .hamburger { display:none; align-items:center; justify-content:center; width:40px; height:40px; border-radius:10px; border:1.5px solid #E7E5E4; background:#fff; cursor:pointer; flex-shrink:0; }
        .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:40; }
        .sidebar { width:220px; min-height:100vh; flex-shrink:0; background:#fff; border-right:1.5px solid #E7E5E4; padding:24px 14px; display:flex; flex-direction:column; gap:6px; position:sticky; top:0; height:100vh; overflow-y:auto; }
        .topbar { display:none; }
        @media (max-width:768px) {
          .sidebar { position:fixed; left:0; top:0; z-index:50; transform:translateX(-100%); transition:transform .28s ease; min-height:100vh; height:100%; }
          .sidebar.open { transform:translateX(0); }
          .sidebar-overlay.open { display:block; }
          .topbar { display:flex; align-items:center; gap:12px; padding:14px 16px; background:#fff; border-bottom:1.5px solid #E7E5E4; position:sticky; top:0; z-index:30; }
          .hamburger { display:flex; }
          .main-wrap { flex-direction:column !important; }
          .main-content { padding:20px 16px !important; }
          .stats-grid { grid-template-columns:1fr 1fr !important; gap:12px !important; }
          .welcome-row { flex-direction:column !important; align-items:flex-start !important; gap:10px !important; }
          .qa-row { flex-wrap:wrap !important; }
        }
        @media (max-width:420px) { .stats-grid { grid-template-columns:1fr !important; } }
        @media (min-width:769px) and (max-width:1100px) { .sidebar { width:190px; } }
      `}</style>

      <div className="main-wrap" style={{ display:"flex", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif", backgroundColor:"#FAF7F4" }}>
        <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={() => setSidebarOpen(false)} />

        {/* SIDEBAR */}
        <aside className={`sidebar ${sidebarOpen?"open":""}`}>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"28px", padding:"0 4px" }}>
            <div style={{ width:"36px", height:"36px", background:"linear-gradient(135deg,#92400E,#D97706)", borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:"18px" }}>🪚</span>
            </div>
            <div>
              <p style={{ fontWeight:"800", fontSize:"15px", color:"#1C1917" }}>Craftigo</p>
              <p style={{ fontSize:"11px", color:"#A8A29E", fontWeight:"500" }}>Admin Panel</p>
            </div>
          </div>
          <p style={{ fontSize:"11px", fontWeight:"700", color:"#C4B5A4", letterSpacing:"0.7px", textTransform:"uppercase", padding:"0 6px", marginBottom:"6px" }}>Menu</p>
          <button className="nav-btn active">🏠 Dashboard</button>
          <button className="nav-btn" onClick={() => { navigate("/admin/users"); setSidebarOpen(false); }}>👥 Users</button>
          <button className="nav-btn" onClick={() => { navigate("/admin/carpenter-labours"); setSidebarOpen(false); }}>🔗 Carpenter & Labour</button>
          <button className="nav-btn" onClick={() => { navigate("/admin/trash"); setSidebarOpen(false); }}>🗑️ Trash</button>
          <div style={{ flex:1 }} />
          <div style={{ padding:"12px", background:"#FEF3C7", borderRadius:"12px", border:"1px solid #FDE68A", marginBottom:"10px" }}>
            <p style={{ fontSize:"12px", fontWeight:"700", color:"#92400E" }}>🛡️ Admin Access</p>
            <p style={{ fontSize:"11px", color:"#B45309", marginTop:"2px" }}>Full permissions</p>
          </div>
          <button className="nav-btn danger" onClick={() => { localStorage.clear(); navigate("/login"); }}>🚪 Logout</button>
        </aside>

        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
          <div className="topbar">
            <button className="hamburger" onClick={() => setSidebarOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6" x2="21" y2="6" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="18" x2="21" y2="18" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <span style={{ fontWeight:"800", fontSize:"16px", color:"#1C1917" }}>🪚 Craftigo Admin</span>
          </div>

          <main className="main-content" style={{ padding:"32px 32px", flex:1, overflowY:"auto" }}>
            <div className="welcome-row" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"8px" }}>
              <div>
                <p style={{ fontSize:"13px", color:"#A8A29E", fontWeight:"500", marginBottom:"3px" }}>Good day 👋</p>
                <h1 style={{ fontSize:"26px", fontWeight:"800", color:"#1C1917", letterSpacing:"-0.5px" }}>Welcome, {adminName}</h1>
              </div>
              <div style={{ padding:"8px 14px", background:"linear-gradient(135deg,#FEF3C7,#FDE68A)", border:"1px solid #D97706", borderRadius:"20px", flexShrink:0 }}>
                <span style={{ fontSize:"11px", fontWeight:"700", color:"#92400E" }}>🛡️ ADMIN</span>
              </div>
            </div>

            <div style={{ height:"1px", background:"#E7E5E4", margin:"20px 0" }} />

            <p style={{ fontSize:"11px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.8px", textTransform:"uppercase", marginBottom:"14px" }}>Platform Overview</p>

            {/* Stat cards */}
            <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"14px", marginBottom:"28px" }}>
              {cards.map((c, i) => (
                <div key={c.label} className="stat-card" style={{ animationDelay:`${i*0.07}s` }} onClick={() => navigate(c.path)}>
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:"3px", background:c.color, borderRadius:"16px 16px 0 0" }} />
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"14px" }}>
                    <div style={{ width:"44px", height:"44px", borderRadius:"12px", background:c.bg, border:`1.5px solid ${c.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px" }}>{c.emoji}</div>
                    {c.value !== null && <span style={{ fontSize:"24px", fontWeight:"800", color:c.color }}>{loading?"—":c.value}</span>}
                    {c.cta && <span style={{ fontSize:"12px", fontWeight:"700", color:c.color, background:c.bg, padding:"4px 10px", borderRadius:"20px" }}>Open →</span>}
                  </div>
                  <p style={{ fontSize:"14px", fontWeight:"700", color:"#1C1917", marginBottom:"3px" }}>{c.label}</p>
                  <p style={{ fontSize:"12px", color:"#A8A29E", fontWeight:"500" }}>{c.desc}</p>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"16px", padding:"20px", marginBottom:"16px" }}>
              <p style={{ fontSize:"12px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.7px", textTransform:"uppercase", marginBottom:"14px" }}>Quick Actions</p>
              <div className="qa-row" style={{ display:"flex", flexWrap:"wrap", gap:"10px" }}>
                {quickActions.map(q => (
                  <button key={q.label} className="qa-btn" onClick={q.action}><span>{q.emoji}</span>{q.label}</button>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"16px", padding:"24px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"20px" }}>
                <div>
                  <p style={{ fontSize:"12px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.7px", textTransform:"uppercase", marginBottom:"4px" }}>Analytics</p>
                  <h3 style={{ fontSize:"16px", fontWeight:"800", color:"#1C1917" }}>User Distribution</h3>
                </div>
                <div style={{ padding:"7px 13px", background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:"20px" }}>
                  <span style={{ fontSize:"11px", fontWeight:"700", color:"#92400E" }}>🪚 vs 🔨</span>
                </div>
              </div>
              {loading ? (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"220px" }}>
                  <div style={{ width:"32px", height:"32px", border:"3px solid #FDE68A", borderTop:"3px solid #D97706", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
                </div>
              ) : stats.carpenters===0 && stats.labours===0 ? (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"220px", gap:"10px" }}>
                  <span style={{ fontSize:"36px" }}>📊</span>
                  <p style={{ fontSize:"14px", fontWeight:"600", color:"#78716C" }}>No data to display yet</p>
                </div>
              ) : (
                <div style={{ display:"flex", alignItems:"center", gap:"32px", flexWrap:"wrap" }}>
                  <div style={{ position:"relative", width:"200px", height:"200px", flexShrink:0, margin:"0 auto" }}>
                    <Pie data={chartData} options={chartOptions} />
                  </div>
                  <div style={{ flex:1, minWidth:"160px", display:"flex", flexDirection:"column", gap:"12px" }}>
                    {[
                      { label:"Carpenters", count:stats.carpenters, color:"#D97706", bg:"#FEF3C7", border:"#FDE68A", emoji:"🪚" },
                      { label:"Labours",    count:stats.labours,    color:"#0369A1", bg:"#E0F2FE", border:"#BAE6FD", emoji:"🔨" },
                    ].map(item => {
                      const t   = stats.carpenters + stats.labours;
                      const pct = t ? Math.round((item.count/t)*100) : 0;
                      return (
                        <div key={item.label} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", background:item.bg, border:`1.5px solid ${item.border}`, borderRadius:"12px" }}>
                          <span style={{ fontSize:"20px" }}>{item.emoji}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"6px" }}>
                              <span style={{ fontSize:"13px", fontWeight:"700", color:"#1C1917" }}>{item.label}</span>
                              <span style={{ fontSize:"13px", fontWeight:"800", color:item.color }}>{item.count}</span>
                            </div>
                            <div style={{ height:"6px", background:"rgba(0,0,0,.08)", borderRadius:"99px", overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${pct}%`, background:item.color, borderRadius:"99px", transition:"width .6s ease" }} />
                            </div>
                            <span style={{ fontSize:"11px", color:item.color, fontWeight:"600", marginTop:"3px", display:"block" }}>{pct}% of workforce</span>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ padding:"10px 14px", background:"#F9F9F9", border:"1px solid #E7E5E4", borderRadius:"10px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontSize:"12px", fontWeight:"600", color:"#78716C" }}>Total workforce</span>
                      <span style={{ fontSize:"15px", fontWeight:"800", color:"#1C1917" }}>{stats.carpenters+stats.labours}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}